import React, {
  forwardRef,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import Loader from '../../../../components/Loader';
import { AuthContext, Role } from '../../../../context/AuthProvider';
import { usernameFullNameOrEmail } from '../../../../helpers/username';
import { StudyGroup } from '../../hooks/api/types';
import useWindowSize from '../../hooks/useWindowSize';
import useAssessment from './hooks/useAssessment';
import TeacherAssessment from './TeacherAssessment';

const timestamp = new Date().getTime();

interface AssessmentContentProps {
  content: any;
  studyGroup: StudyGroup;
  setIsSaveAvailable: (isSaveAvailable: boolean) => void;
  setIsSaving: (isSaving: boolean) => void;
}

const AssessmentContent = forwardRef(
  (props: AssessmentContentProps, iframeRef: any) => {
    const { content, studyGroup, setIsSaveAvailable, setIsSaving } = props;
    const [iframeHeight, setIframeHeight] = useState(1000);
    const user = useContext(AuthContext);
    const [displayUserPk, setDisplayUserPk] = useState(user?.pk);
    const interval = useRef<any>(null);

    const { width, height } = useWindowSize();

    const isTeacher = user?.roles.includes(Role.teacher);

    const {
      studentTeams,
      assessment,
      currentStudentTeam,
      isLoading,
      isContentChanged,
    } = useAssessment({
      displayUserPk,
      setIsSaving,
      iframe: iframeRef,
      studyGroupPk: studyGroup?.pk,
    });

    useEffect(() => {
      if (isContentChanged) {
        window.onbeforeunload = () => {
          return true;
        };
      } else {
        window.onbeforeunload = null;
      }
      return () => {
        window.onbeforeunload = null;
      };
    }, [isContentChanged]);

    useEffect(() => {
      interval.current = setInterval(() => {
        resizeIFrameToFitContent();
      }, 1500);
      return () => {
        if (interval.current) {
          clearInterval(interval.current);
        }
      };
    }, []);

    useEffect(() => {
      resizeIFrameToFitContent();
    }, [width, height]);

    useEffect(() => {
      setIsSaveAvailable(isContentChanged);
    }, [isContentChanged]);

    const resizeIFrameToFitContent = () => {
      if (!iframeRef.current?.contentWindow.document.body) {
        return;
      }
      const h = iframeRef.current.contentWindow.document.body.scrollHeight;
      setIframeHeight(h);

      iframeRef.current.height = h;
      iframeRef.current.style.minHeight = h + ' px !important';
    };

    const onCmsContentLoad = () => {
      resizeIFrameToFitContent();
    };

    const isGroupAssessment = assessment?.fields.is_group;

    return (
      <div className="assessment_content_container">
        <div style={{ marginLeft: 16 }}>
          {!isLoading &&
            isGroupAssessment &&
            (currentStudentTeam?.fields ? (
              <div>
                <h3>{currentStudentTeam.fields.name}</h3>
                <p>
                  <span>team leader: </span>
                  <b>
                    {(() => {
                      const member = studyGroup.students.find(
                        (student: any) =>
                          student.pk === currentStudentTeam.fields.team_leader,
                      );
                      if (member) {
                        return usernameFullNameOrEmail(member);
                      }
                      return '[removed from class]';
                    })()}
                  </b>
                </p>
                <p>
                  <span>members: </span>
                  <b>
                    {currentStudentTeam.fields.members
                      .map((memberPk: string, index: number) => {
                        const member = studyGroup.students.find(
                          (student: any) => student.pk === memberPk,
                        );
                        if (member) {
                          return usernameFullNameOrEmail(member);
                        }
                        return '[removed from class]';
                      })
                      .join(', ')}
                  </b>
                </p>
              </div>
            ) : (
              <h3>No team</h3>
            ))}

          {isTeacher && assessment && studentTeams && studyGroup && (
            <TeacherAssessment
              isGroupAssessment={isGroupAssessment}
              studentTeams={studentTeams}
              students={studyGroup.students}
              setDisplayUserPk={(userId) => setDisplayUserPk(userId)}
            />
          )}
          {isLoading && <Loader />}
        </div>

        <iframe
          title={content['title']}
          ref={iframeRef}
          height={iframeHeight}
          style={{
            height: iframeHeight,
            marginBottom: 40,
            overflow: 'hidden',
            borderBottom: '1px solid rgba(213,214,222,0.5)',
          }}
          width="100%"
          src={`/assessment/${content['pk']}/?timestamp=${timestamp}`}
          frameBorder="0"
          allowFullScreen
          className="cms_content"
          onLoad={() => onCmsContentLoad()}
        />
      </div>
    );
  },
);
export default AssessmentContent;
